import { useMemo } from 'react';

import { useAtomValue } from 'jotai';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import { IInvestmentDataModel } from '../../../data-models/investment.data-model';
import { IMetricsDataModel } from '../../../data-models/metrics.data-model';
import { COMPANY_VIEW_TYPE } from '../../../data-models/view-config.data-model';
import { ViewModel } from '../../../models/view.model';
import { fundsByIdMapAtom } from '../../../services/state/AppConfigStateJ';
import { KPI, KpiConfigMeta } from '../../../types';
import { getForesightStore } from '../../../util/jotai-store';
import { useCompanyMapLookup } from '../../../services/hooks/useSaveCompanyData';
import { filterMetricsTransactionsAndFundDataByFund, KpiCalculator } from '../services/kpi/KpiCalculator';
import { QuartersBuilder } from '../services/QuartersBuilder';
import { selectedViewDatePFJ } from '../state/PageStateJ';
import { useCurrentViewMetrics } from './useCurrentViewMetrics';

export interface KPIMeta {
  isLoading: boolean;
  isFetching: boolean;
  kpis: KPI | null;
  previousQuarterKPIs: KPI | null;
  kpiMetas: KpiConfigMeta[]; // not needed if we decide to always calculate all kpis
}

export const useKPIs = (view: ViewModel): KPIMeta => {
  const date = useAtomValue(selectedViewDatePFJ);
  const fundsById = getForesightStore().get(fundsByIdMapAtom);
  const previousQuarter = useMemo(() => QuartersBuilder.getPreviousQuarterEnd(date), [date]);
  const { metrics, previousQuarterMetrics, filteredMetrics } = useCurrentViewMetrics(view);
  const getCompany = useCompanyMapLookup();
  const companiesMap = useMemo(() => {
    const companyIds = new Set(
      [...(previousQuarterMetrics ?? []), ...(metrics ?? [])].map((m) => m.companyId)
    );
    return [...companyIds].reduce((map, id) => {
      const company = getCompany(id);
      if (company) {
        map.set(company.id, company);
      }
      return map;
    }, new Map<number, ICompanyDataModel>());
  }, [metrics, previousQuarterMetrics, getCompany]);

  // Computed in render rather than in an effect, so no paint can show tiles built from earlier inputs.
  const { kpis, previousQuarterKPIs } = useMemo(() => {
    let calculatedKPIs = {} as KPI;
    let previousQuarterKPIs = {} as KPI;
    const selectedFunds =
      view.filteredFunds?.reduce((res, fund) => {
        return res.add(fund.id);
      }, new Set<number>()) ?? new Set();

    if (filteredMetrics) {
      let dataFilteredByFund = filteredMetrics;
      if (view.companyViewType === COMPANY_VIEW_TYPE.ROUND_TRACKER && selectedFunds !== null) {
        dataFilteredByFund = filterMetricsTransactionsAndFundDataByFund(
          dataFilteredByFund as IMetricsDataModel[],
          selectedFunds,
          fundsById
        );
      }

      calculatedKPIs = KpiCalculator.calculate(
        {
          date: date || new Date(),
          filteredData: dataFilteredByFund as IInvestmentDataModel[],
          metrics: dataFilteredByFund as IMetricsDataModel[],
        },
        view.companyViewType,
        companiesMap
      );
    }

    // The grid publishes its rows in a transition, so a render can pair an empty current set with a
    // populated previous quarter. Comparing against it would report a -100% change.
    if (previousQuarterMetrics.length > 0 && filteredMetrics?.length) {
      let dataFilteredByFund = previousQuarterMetrics;
      if (view.companyViewType === COMPANY_VIEW_TYPE.ROUND_TRACKER && selectedFunds !== null) {
        dataFilteredByFund = filterMetricsTransactionsAndFundDataByFund(
          previousQuarterMetrics as IMetricsDataModel[],
          selectedFunds,
          fundsById
        );
      }

      previousQuarterKPIs = KpiCalculator.calculate(
        {
          date: previousQuarter,
          filteredData: dataFilteredByFund as IInvestmentDataModel[],
          metrics: dataFilteredByFund as IMetricsDataModel[],
        },
        view.companyViewType,
        companiesMap
      );
    }

    return { kpis: calculatedKPIs, previousQuarterKPIs };
  }, [
    date,
    filteredMetrics,
    view.companyViewType,
    previousQuarterMetrics,
    previousQuarter,
    view.filteredFunds,
    fundsById,
    companiesMap,
  ]);

  return useMemo(
    () => ({
      isLoading: !metrics || !filteredMetrics,
      isFetching: !metrics || !filteredMetrics,
      kpis,
      previousQuarterKPIs,
      kpiMetas: KpiCalculator.getKpiMetas(view.companyViewType),
    }),
    [metrics, filteredMetrics, kpis, previousQuarterKPIs, view.companyViewType]
  );
};
